"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn, toUiString } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface ComboboxOption {
  value: string
  label: string
  shortLabel?: string   // Краткое название (kls_names)
  fullLabel?: string    // Полное название для tooltip (kls_namef)
  badgeLabel?: string   // Опциональный бейдж справа (например, тип геометрии)
  badgeClassName?: string // Tailwind-классы бейджа
  indentLevel?: number  // Уровень вложенности для иерархии
  disabled?: boolean
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string | undefined) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  allowClear?: boolean
  clearText?: string
  /**
   * `client` — локальный filter по options (по умолчанию).
   * `server` — options уже отфильтрованы родителем; вызов `onSearchChange` при вводе.
   */
  filterMode?: "client" | "server"
  onSearchChange?: (search: string) => void
}

type RenderEntry =
  | { type: "header"; key: string; label: string }
  | { type: "option"; option: ComboboxOption };

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Выберите значение...",
  searchPlaceholder = "Поиск...",
  emptyText = "Ничего не найдено",
  className,
  disabled = false,
  allowClear = true,
  clearText = "Очистить",
  filterMode = "client",
  onSearchChange,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const onSearchChangeRef = React.useRef(onSearchChange)
  onSearchChangeRef.current = onSearchChange

  const safeOptions = React.useMemo(
    () =>
      options.map((o) => ({
        ...o,
        value: toUiString(o.value),
        label: toUiString(o.label),
        shortLabel:
          o.shortLabel !== undefined && o.shortLabel !== null
            ? toUiString(o.shortLabel)
            : undefined,
        fullLabel:
          o.fullLabel !== undefined && o.fullLabel !== null
            ? toUiString(o.fullLabel)
            : undefined,
        badgeLabel:
          o.badgeLabel !== undefined && o.badgeLabel !== null
            ? toUiString(o.badgeLabel)
            : undefined,
      })),
    [options],
  )

  const selectedOption = safeOptions.find((option) => option.value === value)
  
  // Отображаемое значение: краткое название если есть, иначе label
  const displayValue = selectedOption 
    ? (selectedOption.shortLabel || selectedOption.label)
    : (!value || value === "all") && allowClear 
      ? clearText 
      : placeholder

  const handleSearchChange = React.useCallback(
    (next: string) => {
      setSearchValue(next)
      if (filterMode === "server") {
        onSearchChangeRef.current?.(next)
      }
    },
    [filterMode],
  )

  // Фильтрация опций по поиску (поиск по label, shortLabel, fullLabel)
  const filteredOptions = React.useMemo(() => {
    if (filterMode === "server") return safeOptions
    if (!searchValue.trim()) return safeOptions

    const search = searchValue.toLowerCase()
    return safeOptions.filter((option) => {
      const matchValue = option.value.toLowerCase().includes(search)
      const matchLabel = option.label.toLowerCase().includes(search)
      const matchShort = option.shortLabel?.toLowerCase().includes(search)
      const matchFull = option.fullLabel?.toLowerCase().includes(search)
      return matchValue || matchLabel || matchShort || matchFull
    })
  }, [safeOptions, searchValue, filterMode])

  const renderedEntries = React.useMemo<RenderEntry[]>(() => {
    const list = filteredOptions.filter((option) => {
      if (allowClear && option.value === "all") {
        return false
      }
      return true
    })

    const hasHierarchy = list.some((option) => (option.indentLevel ?? 0) > 0)
    if (!hasHierarchy) {
      return list.map((option) => ({ type: "option", option }))
    }

    const entries: RenderEntry[] = []
    for (let i = 0; i < list.length; i += 1) {
      const current = list[i]
      const indent = current.indentLevel ?? 0

      if (indent === 0) {
        let hasChildren = false
        for (let j = i + 1; j < list.length; j += 1) {
          const nextIndent = list[j].indentLevel ?? 0
          if (nextIndent === 0) break
          hasChildren = true
        }

        if (hasChildren) {
          entries.push({
            type: "header",
            key: `header-${current.value}`,
            label: current.shortLabel || current.label,
          })
          continue
        }
      }

      entries.push({ type: "option", option: current })
    }

    return entries
  }, [filteredOptions, allowClear])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
          title={selectedOption?.fullLabel || selectedOption?.label}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 z-[9999]" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onValueChange?.(undefined)
                    setOpen(false)
                    handleSearchChange("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !value || value === "all" ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {clearText}
                </CommandItem>
              )}
              {renderedEntries.map((entry) => {
                if (entry.type === "header") {
                  return (
                    <div
                      key={entry.key}
                      className="mx-2 my-1 rounded-sm bg-muted px-2 py-1 text-xs font-medium text-muted-foreground"
                    >
                      {entry.label}
                    </div>
                  )
                }

                const option = entry.option
                const paddingLeft = option.indentLevel ? option.indentLevel * 16 : 0
                const prefix = option.indentLevel && option.indentLevel > 0 ? '└─ ' : ''
                const displayLabel = option.shortLabel || option.label

                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    onSelect={() => {
                      if (option.value === value) {
                        if (allowClear) {
                          onValueChange?.(undefined)
                        }
                      } else {
                        onValueChange?.(option.value)
                      }
                      setOpen(false)
                      handleSearchChange("")
                    }}
                    style={{ paddingLeft: `${paddingLeft + 8}px` }}
                    title={option.fullLabel || option.label}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span className="truncate" title={option.fullLabel || option.label}>
                        {prefix}{displayLabel}
                      </span>
                      {option.badgeLabel ? (
                        <span
                          className={cn(
                            "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium",
                            option.badgeClassName || "bg-muted text-muted-foreground",
                          )}
                        >
                          {option.badgeLabel}
                        </span>
                      ) : null}
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

